import {Location} from '@angular/common';
import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {ErrorHandlerUtils} from '@app/core/utils/error-handler.utils';
import {getCustomerProfile} from '@app/shared/state/header/header.action';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {MessageService} from 'primeng/api';
import {catchError, map, of, switchMap, tap} from 'rxjs';
import {IAuthenticatorSetupVerifyErrorResponse} from '../models/authenticator-setup-verify.response.model';
import {MfaSetupService} from '../services/mfa-setup.service';
import {
  cancelAuthenticatorSetup,
  cancelAuthenticatorSetupError,
  cancelAuthenticatorSetupSuccess,
  loadAuthenticatorSetup,
  loadAuthenticatorSetupError,
  loadAuthenticatorSetupSuccess,
  resetAuthenticatorSetupState,
  verifyAuthenticatorSetup,
  verifyAuthenticatorSetupError,
  verifyAuthenticatorSetupSuccess,
} from './authenticator-setup.actions';

@Injectable()
export class MfaSetupEffects {
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly mfaSetupService = inject(MfaSetupService);
  private readonly globalToast = 'global-toast';
  private readonly translate = inject(TranslateService);

  loadAuthenticatorSetup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAuthenticatorSetup),
      switchMap(() =>
        this.mfaSetupService.authenticatorSetup().pipe(
          map(response => loadAuthenticatorSetupSuccess({response})),
          catchError((error: unknown) => {
            const message =
              error instanceof Error ? error.message : this.translate.instant('MfaSetup.InitiationFailed');
            ErrorHandlerUtils.showErrorMessage(
              this.messageService,
              this.globalToast,
              this.translate.instant('MfaSetup.InitiationFailed')
            );
            return of(loadAuthenticatorSetupError({error: message}));
          })
        )
      )
    )
  );

  cancelAuthenticatorSetup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelAuthenticatorSetup),
      switchMap(() =>
        this.mfaSetupService.authenticatorSetupCancel().pipe(
          tap(() => this.location.back()),
          map(() => cancelAuthenticatorSetupSuccess()),
          catchError((error: unknown) => {
            const message =
              error instanceof Error ? error.message : this.translate.instant('MfaSetup.Cancel2FASetupFailed');
            ErrorHandlerUtils.showErrorMessage(
              this.messageService,
              this.globalToast,
              this.translate.instant('MfaSetup.Cancel2FASetupFailed')
            );
            return of(cancelAuthenticatorSetupError({error: message}));
          })
        )
      )
    )
  );

  verifyAuthenticatorSetup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyAuthenticatorSetup),
      switchMap(({code}) =>
        this.mfaSetupService.authenticatorSetupVerify({code}).pipe(
          map(response => {
            this.store.dispatch(getCustomerProfile());
            return verifyAuthenticatorSetupSuccess({response});
          }),
          catchError((error: unknown) => {
            const failureResponse =
              (error as {error?: IAuthenticatorSetupVerifyErrorResponse})?.error ??
              (error as IAuthenticatorSetupVerifyErrorResponse | undefined);
            const message =
              failureResponse?.message ?? this.translate.instant('MfaSetup.VerifyAuthenticatorSetupOTPFailed');

            ErrorHandlerUtils.showErrorMessage(this.messageService, this.globalToast, message);

            if (failureResponse?.setupCancelled) {
              this.store.dispatch(resetAuthenticatorSetupState());
              this.router.navigate(['/auth/security-settings'], {replaceUrl: true});
            }

            return of(verifyAuthenticatorSetupError({error: message}));
          })
        )
      )
    )
  );

  navigateAfterVerificationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(verifyAuthenticatorSetupSuccess),
        tap(() => this.router.navigate(['/mfa-setup/recovery-codes'], {replaceUrl: true}))
      ),
    {dispatch: false}
  );
}
